import {ApplicationGeneric} from "../../../../../services/model/new-application.model";

const BLACK_COLOR = 'rgba(12, 38, 51, 1)';

interface ImageResolutionCache {
	low: HTMLImageElement | null;
	medium: HTMLImageElement | null;
	high: HTMLImageElement | null;
	original: HTMLImageElement | null;
}

interface LoadingPromises {
	low: Promise<HTMLImageElement> | null;
	medium: Promise<HTMLImageElement> | null;
	high: Promise<HTMLImageElement> | null;
	original: Promise<HTMLImageElement> | null;
}

interface AppImageInfo {
	app: ApplicationGeneric;
	x: number;
	y: number;
	gridSize: number;
	currentResolution: ResolutionLevel;
}

type ResolutionLevel = 'low' | 'medium' | 'high' | 'original';

const multiResImageCache = new Map<string, ImageResolutionCache>();
const multiResLoadingPromises = new Map<string, LoadingPromises>();

const appImageRegistry = new Map<string, AppImageInfo[]>();

const ZOOM_THRESHOLDS = {
	low: 0.8,
	medium: 1.3,
	high: 1.7,
	original: 2
};

const RESOLUTION_SIZES = {
	low: { width: 24, height: 24 },
	medium: { width: 48, height: 48 },
	high: { width: 96, height: 96 },
	original: null
};

function getResolutionLevel(scale: number): ResolutionLevel {
	if (scale < ZOOM_THRESHOLDS.low) return 'low';
	if (scale < ZOOM_THRESHOLDS.medium) return 'medium';
	if (scale < ZOOM_THRESHOLDS.high) return 'high';
	return 'original';
}

function createResizedImage(originalImage: HTMLImageElement, targetWidth: number, targetHeight: number): HTMLImageElement {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d')!;

	canvas.width = targetWidth;
	canvas.height = targetHeight;

	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = 'high';

	ctx.drawImage(originalImage, 0, 0, targetWidth, targetHeight);

	const resizedImage = new Image();
	resizedImage.src = canvas.toDataURL('image/png', 0.9);

	return resizedImage;
}

function loadImageAtResolution(src: string, resolution: ResolutionLevel): Promise<HTMLImageElement> {
	const cache = multiResImageCache.get(src);
	if (cache && cache[resolution]) {
		return Promise.resolve(cache[resolution]!);
	}

	const loadingPromises = multiResLoadingPromises.get(src);
	if (loadingPromises && loadingPromises[resolution]) {
		return loadingPromises[resolution]!;
	}

	if (!multiResImageCache.has(src)) {
		multiResImageCache.set(src, {
			low: null,
			medium: null,
			high: null,
			original: null
		});
	}

	if (!multiResLoadingPromises.has(src)) {
		multiResLoadingPromises.set(src, {
			low: null,
			medium: null,
			high: null,
			original: null
		});
	}

	const loadingPromise = new Promise<HTMLImageElement>((resolve, reject) => {
		const cache = multiResImageCache.get(src)!;
		const promises = multiResLoadingPromises.get(src)!;

		if (!cache.original && !promises.original) {
			promises.original = new Promise<HTMLImageElement>((resolveOriginal, rejectOriginal) => {
				const originalImg = new Image();
				originalImg.onload = () => {
					cache.original = originalImg;
					promises.original = null;
					resolveOriginal(originalImg);
				};
				originalImg.onerror = () => {
					promises.original = null;
					rejectOriginal(new Error(`Failed to load original image: ${src}`));
				};
				originalImg.crossOrigin = 'anonymous';
				originalImg.src = src;
			});
		}

		const processImage = (originalImage: HTMLImageElement) => {
			if (resolution === 'original') {
				cache.original = originalImage;
				resolve(originalImage);
				return;
			}

			const targetSize = RESOLUTION_SIZES[resolution];
			if (!targetSize) {
				resolve(originalImage);
				return;
			}

			try {
				const resizedImage = createResizedImage(originalImage, targetSize.width, targetSize.height);

				resizedImage.onload = () => {
					cache[resolution] = resizedImage;
					promises[resolution] = null;
					resolve(resizedImage);
				};

				resizedImage.onerror = () => {
					promises[resolution] = null;
					reject(new Error(`Failed to create resized image: ${src} at ${resolution}`));
				};

			} catch (error) {
				promises[resolution] = null;
				reject(error);
			}
		};

		if (cache.original) {
			processImage(cache.original);
		} else if (promises.original) {
			promises.original.then(processImage).catch(reject);
		} else {
			promises.original!.then(processImage).catch(reject);
		}
	});

	multiResLoadingPromises.get(src)![resolution] = loadingPromise;
	return loadingPromise;
}

export async function preloadAppImages(apps: ApplicationGeneric[], scale: number = 1.0): Promise<void> {
	const uniqueLogos = [...new Set(apps.map(app => app.logo))];
	const resolution = getResolutionLevel(scale);

	try {
		await Promise.all(uniqueLogos.map(logo => loadImageAtResolution(logo, resolution)));

		// Charger les autres résolutions en arrière-plan
		const allResolutions: ResolutionLevel[] = ['low', 'medium', 'high', 'original'];
		const otherResolutions = allResolutions.filter(res => res !== resolution);
		otherResolutions.forEach(otherRes => {
			uniqueLogos.forEach(logo => {
				loadImageAtResolution(logo, otherRes).catch(() => {});
			});
		});

	} catch (error) {
		console.warn('Erreur lors du préchargement des images:', error);
	}
}

export async function drawCapacitiesApp(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	apps: ApplicationGeneric[],
	dataId: string,
	x: number,
	y: number,
	gridSize: number,
	angle: number,
	registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
	isButtonHovered: (id: string) => boolean,
	scale: number,
): Promise<void> {

	await preloadAppImages(apps, scale);

	const baseX: number = x + .5;
	let baseY: number = y + .5 + gridSize * 2;

	const appInfos: AppImageInfo[] = [];

	if (apps.length > 0) {
		for (let i = 1; i <= apps.length; i++) {
			let appX: number, appY: number;

			if (i % 4 === 1) {
				appX = baseX + gridSize;
				appY = baseY;
			} else if (i % 4 === 2) {
				appX = baseX + gridSize * 3;
				appY = baseY;
			} else if (i % 4 === 3) {
				appX = baseX + gridSize * 5;
				appY = baseY;
			} else { // i % 4 === 0
				appX = baseX + gridSize * 7;
				appY = baseY;
				baseY += gridSize * 2;
			}

			appInfos.push({
				app: apps[i-1],
				x: appX,
				y: appY,
				gridSize,
				currentResolution: getResolutionLevel(scale)
			});

			drawApp(ctx, apps[i-1], registerButton, appX, appY, gridSize, angle, scale);
		}
	}

	appImageRegistry.set(dataId, appInfos);
}

export async function updateAppImagesResolution(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	dataId: string,
	newScale: number,
	angle: number,
): Promise<void> {
	const appInfos = appImageRegistry.get(dataId);
	if (!appInfos) return;

	const newResolution = getResolutionLevel(newScale);

	const needsUpdate = appInfos.some(info => info.currentResolution !== newResolution);
	if (!needsUpdate) return;

	const uniqueLogos = [...new Set(appInfos.map(info => info.app.logo))];
	try {
		await Promise.all(uniqueLogos.map(logo => loadImageAtResolution(logo, newResolution)));
	} catch (error) {
		console.warn('Erreur lors du chargement des nouvelles résolutions:', error);
		return;
	}

	for (const appInfo of appInfos) {
		await redrawSingleAppImage(ctx, appInfo, newResolution, angle);
		appInfo.currentResolution = newResolution; // Mettre à jour la résolution actuelle
	}
}

async function redrawSingleAppImage(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	appInfo: AppImageInfo,
	newResolution: ResolutionLevel,
	angle: number,
): Promise<void> {
	const { app, x, y, gridSize } = appInfo;

	const imageX = x + 3.5;
	const imageY = y + 3.5;
	const imageSize = gridSize - 7;

	ctx.clearRect(imageX, imageY, imageSize, imageSize);

	ctx.save();
	ctx.fillStyle = 'rgba(255, 255, 255, 1)';
	ctx.beginPath();
	ctx.rect(imageX, imageY, imageSize, imageSize);
	ctx.fill();
	ctx.restore();

	const cache = multiResImageCache.get(app.logo);
	let image: HTMLImageElement | null = null;

	if (cache && cache[newResolution]) {
		image = cache[newResolution];
	} else if (cache) {
		const fallbackOrder: ResolutionLevel[] = ['high', 'medium', 'original', 'low'];
		for (const fallbackRes of fallbackOrder) {
			if (cache[fallbackRes]) {
				image = cache[fallbackRes];
				break;
			}
		}
	}

	if (image) {
		ctx.save();
		ctx.beginPath();
		ctx.roundRect(imageX, imageY, imageSize, imageSize, [angle]);
		ctx.clip();
		ctx.drawImage(image, imageX, imageY, imageSize, imageSize);
		ctx.restore();
	}
}

export async function updateAllAppImagesForScale(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	newScale: number,
	angle: number,
): Promise<void> {
	// Mettre à jour toutes les capacités enregistrées
	const updatePromises: Promise<void>[] = [];

	for (const dataId of appImageRegistry.keys()) {
		updatePromises.push(updateAppImagesResolution(ctx, dataId, newScale, angle));
	}

	await Promise.all(updatePromises);
}

function drawApp(
	ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
	app: ApplicationGeneric | null,
	registerButton: (id: string, name: string, type: string, x: number, y: number, width: number, height: number) => void,
	x: number,
	y: number,
	gridSize: number,
	angle: number,
	scale: number,
): void {

	if (app === null) {
		return;
	}

	registerButton(app.id, app.name, 'app-button', x, y, gridSize, gridSize);

	// Dessiner le rectangle
	ctx.beginPath();
	ctx.fillStyle = 'rgba(255, 255, 255, 1)';
	ctx.strokeStyle = 'rgba(181, 181, 195, 1)';
	ctx.lineWidth = 1;
	ctx.roundRect(x, y, gridSize, gridSize, [angle]);
	ctx.stroke();
	ctx.fill();

	const resolution = getResolutionLevel(scale);
	const cache = multiResImageCache.get(app.logo);
	let image: HTMLImageElement | null = null;

	if (cache && cache[resolution]) {
		image = cache[resolution];
	} else if (cache) {
		const fallbackOrder: ResolutionLevel[] = ['high', 'medium', 'original', 'low'];
		for (const fallbackRes of fallbackOrder) {
			if (cache[fallbackRes]) {
				image = cache[fallbackRes];
				break;
			}
		}
	}

	if (image) {
		ctx.save();
		ctx.beginPath();
		ctx.roundRect(x + 3.5, y + 3.5, gridSize - 7, gridSize - 7, [angle]);
		ctx.clip();
		ctx.drawImage(image, x + 3.5, y + 3.5, gridSize - 7, gridSize - 7);
		ctx.restore();
	}

	let text: string = app.name;
	const maxWidth = gridSize * 2 - (gridSize/6);

	ctx.font = '500 12px proxima-nova';
	const textMetrics = ctx.measureText(text);
	const textWidth = textMetrics.width;

	if (textWidth > maxWidth) {
		let truncatedText = text;
		while (ctx.measureText(truncatedText + '...').width > maxWidth && truncatedText.length > 0) {
			truncatedText = truncatedText.slice(0, -1);
		}
		text = truncatedText + '...';
		registerButton("01", app.name, 'tool-tip-name', x - gridSize/1.9, y + gridSize + 5, gridSize * 1.9, gridSize / 3.5);
	}

	ctx.fillStyle = BLACK_COLOR;
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.letterSpacing = "-0.1px";
	ctx.fillText(text, x + gridSize / 2, y + gridSize + 12);
}
export function clearImageCache(): void {
	multiResImageCache.clear();
	multiResLoadingPromises.clear();
	appImageRegistry.clear();
}
